import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-quartz.css';
import { AgGridReact, CustomCellRendererProps } from '@ag-grid-community/react';
import {
  ColDef,
  GetRowIdParams,
  GridReadyEvent,
  GridSizeChangedEvent,
  KeyCreatorParams,
  ValueFormatterParams
} from '@ag-grid-community/core';
import {
  formatLineItemType,
  LineItemType,
  lineItemTypeOptions
} from 'shared/src/domain/line-item/line-item-types';
import {
  allTactics,
  allUnitPriceTypes,
  Channel
} from 'shared/src/domain/line-item/line-item-channels';
import { useChannelRatesContext } from '@/features/strategy/channel-rates-context';
import { formatDateString } from 'shared/src/utils/date/date-utils';
import { useUrlTableState } from '@/components/tables/table-state-persistence';
import { DBStrategy } from 'shared/src/types';
import {
  isAgencyMarkupStrategy,
  isReferralCommissionStrategy
} from '@/features/strategy/strategy-utils';
import {
  AgencyMarkupRenderer,
  AgencyMarkupTooltip,
  CellRendererProps,
  DeliveryPacingRenderer,
  EffectivePriceRenderer,
  EffectivePriceTooltip,
  EffectiveUnitPriceRenderer,
  EffectiveUnitPriceTooltip,
  EstimatedUnitsRenderer,
  EstimatedUnitsTooltip,
  GrossPriceRenderer,
  GrossPriceTooltip,
  GrossUnitPriceRenderer,
  GrossUnitPriceTooltip,
  MarginRenderer,
  MediaBudgetRenderer,
  MediaBudgetTooltip,
  NullPacingDetails,
  PlannedMarginCellRenderer,
  PlannedMarginTooltip,
  PlannedUnitCostRenderer,
  PlannedUnitCostTooltip,
  PriceCellRenderer,
  PriceTooltip,
  ReferralFeeRenderer,
  ReferralFeeTooltip,
  SpendPacingRenderer,
  TargetMarginCellRenderer,
  TargetMarginTooltip,
  TargetUnitCostCellRenderer,
  TargetUnitCostTooltip,
  TargetUnitPriceCellRenderer,
  TargetUnitPriceTooltip,
  UnitPriceCellRenderer,
  UnitPriceTooltip,
  ViewButtonCellRenderer
} from '@/features/line-items/line-item-table-cell-components';
import { generateGraph } from '@/features/proposals/line-item-table-utils';
import {
  ExecutionLineItemTrpcResponse,
  ExecutionLineItemWithMetricsTrpcResponse
} from 'shared/src/trpc-types/line-item-trpc-types';
import { ExecutionLineItemActions } from '@/features/line-items/execution-line-item-actions';
import { ExecutionContext } from '@/features/executions/execution-context';
import { PriceAllocationProgress } from '@/features/line-items/price-allocation-progress';

const defaultColDef: ColDef = {
  maxWidth: 250,
  suppressHeaderContextMenu: true,
  suppressHeaderMenuButton: true,
  editable: false
};

function ExecutionAllocationPanel() {
  const context = useContext(ExecutionContext);

  // Guard against AG Grid rendering status panel before context is ready
  if (!context) {
    return <div className="w-full p-3" />;
  }

  const { execution, lineItems } = context;
  const allocated = lineItems.reduce((sum, li) => sum + (li.price ?? 0), 0);

  return (
    <div className="w-full p-3">
      <PriceAllocationProgress
        total={execution.price ?? 0}
        allocated={allocated}
        label="Campaign Price"
      />
    </div>
  );
}

type Props = {
  lineItems: ExecutionLineItemWithMetricsTrpcResponse[];
  execution: DBStrategy;
  setIsEditing: (lineItemId: string) => void;
};

export function ExecutionLineItemsTable({ lineItems, execution, setIsEditing }: Props) {
  const { initialState, storeGridState } = useUrlTableState();
  const gridRef = useRef<AgGridReact<ExecutionLineItemWithMetricsTrpcResponse>>(null);
  const { channels } = useChannelRatesContext();
  const columnDefs: ColDef<ExecutionLineItemWithMetricsTrpcResponse>[] = useMemo(
    () =>
      createColumnDefs(
        channels,
        execution,
        lineItems.some(m => (m.actual_delivered_units || 0) > 0),
        setIsEditing
      ),
    [channels, execution, lineItems, setIsEditing]
  );

  useEffect(() => {
    // ag-grid sometimes does not properly refresh cells after the data
    // has changed, resulting in styling not being applied correctly.
    // this effect forces a refresh when lineItems changes, resolving the issue
    gridRef.current?.api?.refreshCells({ force: true });
  }, [lineItems]);

  const getRowId = useCallback(
    (params: GetRowIdParams<ExecutionLineItemTrpcResponse>) => params.data.id,
    []
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      gridRef.current?.api?.autoSizeAllColumns();
    }, 250);
    return () => clearTimeout(timeoutId);
  }, [lineItems]);

  const onGridReady = useCallback((event: GridReadyEvent) => {
    // Expose the grid API to the window for Playwright testing
    (window as any).agGridApi = event.api;
  }, []);

  // AGGrid will unpin pinned columns when you resize the window but will not re-pin them
  const onGridSizeChanged = useCallback((e: GridSizeChangedEvent) => {
    e.api.setColumnsPinned(['name'], 'left');
    e.api.setColumnsPinned(['actions'], 'right');
  }, []);

  return (
    <div className="flex h-full w-full flex-col">
      <div className="ag-theme-quartz" style={{ height: '100%', width: '100%' }}>
        <AgGridReact
          ref={gridRef}
          rowData={lineItems}
          columnDefs={columnDefs}
          gridOptions={{ headerHeight: 36, rowHeight: 42 }}
          suppressRowVirtualisation={true}
          suppressColumnVirtualisation={true}
          stopEditingWhenCellsLoseFocus={true}
          defaultColDef={defaultColDef}
          domLayout={'normal'}
          getRowId={getRowId}
          tooltipShowDelay={0}
          initialState={initialState}
          onStateUpdated={storeGridState}
          onGridReady={onGridReady}
          onGridSizeChanged={onGridSizeChanged}
          statusBar={{
            statusPanels: [{ statusPanel: ExecutionAllocationPanel, align: 'center' }]
          }}
        />
      </div>
    </div>
  );
}

function createColumnDefs(
  channels: Channel[],
  execution: DBStrategy,
  showMetrics: boolean,
  setIsEditing: (lineItemId: string) => void
): ColDef<ExecutionLineItemWithMetricsTrpcResponse>[] {
  const tactics = allTactics(channels);
  const unitPriceTypes = allUnitPriceTypes(channels);
  const hasReferralCommission = isReferralCommissionStrategy(execution);
  const hasAgencyMarkup = isAgencyMarkupStrategy(execution);

  return [
    {
      cellRenderer: ViewButtonCellRenderer,
      headerName: '',
      cellStyle: { padding: 0 },
      maxWidth: 70,
      pinned: 'left',
      resizable: false,
      suppressMovable: true,
      suppressAutoSize: true
    },
    {
      field: 'name',
      headerName: 'Line Item Name',
      pinned: 'left',
      maxWidth: 500
    },
    {
      field: 'line_item_type',
      headerName: 'Line Item Type',
      maxWidth: 150,
      valueFormatter: params => formatLineItemType(params.value),
      filter: 'agSetColumnFilter',
      filterParams: {
        values: lineItemTypeOptions,
        valueFormatter: (params: ValueFormatterParams<LineItemType>) =>
          formatLineItemType(params.value)
      }
    },
    {
      field: 'delivery_pacing',
      headerName: 'Delivery Pacing',
      hide: !showMetrics,
      cellRenderer: DeliveryPacingRenderer,
      initialWidth: 150
    },
    {
      field: 'spend_pacing',
      headerName: 'Spend Pacing',
      hide: !showMetrics,
      cellRenderer: SpendPacingRenderer,
      initialWidth: 150
    },
    {
      field: 'margin_actual',
      headerName: 'Margin',
      hide: !showMetrics,
      cellRenderer: MarginRenderer,
      initialWidth: 150
    },
    {
      field: 'price',
      headerName: 'Price',
      cellDataType: 'number',
      cellRenderer: PriceCellRenderer,
      filter: 'agNumberColumnFilter',
      filterParams: {
        allowedCharPattern: '\\d\\-\\,'
      },
      tooltipComponent: PriceTooltip,
      tooltipField: 'price'
    },
    {
      field: 'effective_price',
      headerName: 'Effective Price',
      hide: !hasReferralCommission,
      cellDataType: 'number',
      cellRenderer: EffectivePriceRenderer,
      tooltipComponent: EffectivePriceTooltip,
      tooltipComponentParams: { referralCommissionRate: execution.referral_commission_rate },
      tooltipValueGetter: params => params.value || 'no value'
    },
    {
      field: 'referral_commission_amount',
      headerName: 'Referral Fee',
      hide: !hasReferralCommission,
      cellDataType: 'number',
      cellRenderer: ReferralFeeRenderer,
      tooltipComponent: ReferralFeeTooltip,
      tooltipComponentParams: { referralCommissionRate: execution.referral_commission_rate },
      tooltipValueGetter: params => params.value || 'no value'
    },
    {
      field: 'agency_markup_rate',
      headerName: 'Agency Markup',
      hide: !hasAgencyMarkup,
      cellDataType: 'number',
      cellRenderer: AgencyMarkupRenderer,
      tooltipComponent: AgencyMarkupTooltip,
      tooltipComponentParams: { agencyMarkupRate: execution.agency_markup_rate }
    },
    {
      field: 'gross_price',
      headerName: 'Gross Price',
      hide: !hasAgencyMarkup,
      cellDataType: 'number',
      cellRenderer: GrossPriceRenderer,
      tooltipComponent: GrossPriceTooltip,
      tooltipComponentParams: { agencyMarkupRate: execution.agency_markup_rate },
      tooltipValueGetter: params => params.value || 'no value'
    },
    // TODO[mk] - channel, tactic, unit_price_id keyCreators and comparators need to be defined
    {
      field: 'channel_name',
      headerName: 'Channel',
      filter: 'agSetColumnFilter',
      filterParams: {
        values: channels,
        valueFormatter: (params: ValueFormatterParams<Channel>) => params.value.name
      }
    },
    {
      field: 'tactic_name',
      headerName: 'Tactic',
      filter: 'agSetColumnFilter',
      filterParams: {
        values: tactics,
        keyCreator: (params: KeyCreatorParams) => params.value.id,
        valueFormatter: (params: ValueFormatterParams) => params.value.name
      }
    },
    {
      field: 'unit_price_type_name',
      headerName: 'Unit Price Type',
      minWidth: 200,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: unitPriceTypes,
        keyCreator: (params: KeyCreatorParams) => params.value.id,
        valueFormatter: (params: ValueFormatterParams) => params.value.name
      }
    },
    {
      field: 'target_unit_price',
      headerName: 'Target Unit Price',
      cellDataType: 'number',
      tooltipComponent: TargetUnitPriceTooltip,
      tooltipComponentParams: { channels },
      tooltipValueGetter: params => params.value || 'no value',
      cellRenderer: TargetUnitPriceCellRenderer
    },
    {
      field: 'unit_price',
      headerName: 'Unit Price',
      cellDataType: 'number',
      tooltipComponent: UnitPriceTooltip,
      tooltipComponentParams: { channels },
      tooltipValueGetter: params => params.value || 'no value', // need to pass dummy value here so tooltip will still render
      cellRenderer: (params: CellRendererProps<number>) => (
        <UnitPriceCellRenderer channels={channels} {...params} />
      )
    },
    {
      field: 'gross_unit_price',
      headerName: 'Gross Unit Price',
      hide: !hasAgencyMarkup,
      cellDataType: 'number',
      cellRenderer: GrossUnitPriceRenderer,
      tooltipComponent: GrossUnitPriceTooltip,
      tooltipValueGetter: params => params.value || 'no value'
    },
    {
      field: 'effective_unit_price',
      headerName: 'Effective Unit Price',
      hide: !hasReferralCommission,
      cellDataType: 'number',
      cellRenderer: EffectiveUnitPriceRenderer,
      tooltipComponent: EffectiveUnitPriceTooltip,
      tooltipValueGetter: params => params.value || 'no value'
    },
    {
      field: 'estimated_units',
      headerName: 'Estimated Units',
      cellDataType: 'number',
      cellRenderer: EstimatedUnitsRenderer,
      tooltipComponent: EstimatedUnitsTooltip,
      tooltipField: 'estimated_units'
    },
    {
      field: 'media_budget',
      headerName: 'Media Budget',
      cellRenderer: MediaBudgetRenderer,
      tooltipComponent: MediaBudgetTooltip,
      tooltipComponentParams: { referralCommissionRate: execution.referral_commission_rate },
      tooltipField: 'media_budget'
    },
    {
      field: 'target_unit_cost',
      headerName: 'Target Unit Cost',
      cellDataType: 'number',
      tooltipComponent: TargetUnitCostTooltip,
      tooltipComponentParams: { channels },
      tooltipValueGetter: params => params.value || 'no value',
      cellRenderer: TargetUnitCostCellRenderer
    },
    {
      field: 'planned_unit_cost',
      headerName: 'Planned Unit Cost',
      cellRenderer: (params: CellRendererProps<number>) => (
        <PlannedUnitCostRenderer channels={channels} {...params} />
      ),
      tooltipComponent: PlannedUnitCostTooltip,
      tooltipComponentParams: {
        referralCommissionRate: execution.referral_commission_rate,
        channels
      },
      tooltipField: 'planned_unit_cost'
    },
    {
      field: 'target_margin',
      headerName: 'Target Margin',
      cellDataType: 'number',
      tooltipComponent: TargetMarginTooltip,
      tooltipValueGetter: params => params.value || 'no value',
      cellRenderer: TargetMarginCellRenderer
    },
    {
      field: 'planned_margin',
      headerName: 'Planned Margin',
      cellDataType: 'number',
      editable: false, // Now a calculated field, not user-editable
      tooltipComponent: PlannedMarginTooltip,
      tooltipValueGetter: params => params.value || 'no value', // need to pass dummy value here so tooltip will still render
      cellRenderer: (params: CellRendererProps<number>) => (
        <PlannedMarginCellRenderer channels={channels} {...params} />
      )
    },
    {
      cellDataType: 'date',
      field: 'dates.from',
      headerName: 'Start Date',
      valueFormatter: params => formatDateString(params.value)
    },
    {
      cellDataType: 'date',
      field: 'dates.to',
      headerName: 'End Date',
      valueFormatter: params => formatDateString(params.value)
    },
    {
      field: 'pacing_type',
      headerName: 'Pacing Type',
      filter: 'agSetColumnFilter',
      filterParams: { values: ['lifetime', 'custom'] }
    },
    {
      headerName: 'Pacing Details',
      minWidth: 200,
      valueGetter: ({ data }) => (data ? generateGraph(data) : undefined),
      cellRendererParams: { sparklineOptions: { type: 'area' } },
      cellRendererSelector: ({ data }) => {
        if (data?.pacing_type === 'custom') return { component: 'agSparklineCellRenderer' };
        else return { component: NullPacingDetails };
      }
    },
    {
      field: 'geo'
    },
    {
      field: 'targeting'
    },
    {
      field: 'audience'
    },
    {
      field: 'ad_formats',
      headerName: 'Ad Formats'
    },
    {
      field: 'purchase_order_number',
      headerName: 'PO Number (optional)'
    },
    {
      field: 'notes',
      headerName: 'Team Notes (optional)',
      maxWidth: 300
    },
    {
      field: 'media_platforms',
      headerName: 'Media Platforms',
      cellRenderer: ({ data }: CustomCellRendererProps<ExecutionLineItemTrpcResponse>) =>
        data?.media_platforms.map(t => t.name).join(',')
    },
    {
      field: 'media_traders',
      headerName: 'Media Traders',
      cellRenderer: ({ data }: CustomCellRendererProps<ExecutionLineItemTrpcResponse>) =>
        data?.media_traders.map(t => t.name).join(','),
      comparator: (a, b) => {
        if (a.length === 0 && b.length === 0) return 0;
        if (a.length === 0) return -1;
        if (b.length === 0) return 1;
        return a[0].name.localeCompare(b[0].name);
      }
    },
    {
      cellRenderer: ({ data }: CustomCellRendererProps<ExecutionLineItemTrpcResponse>) =>
        data && <ExecutionLineItemActions data={data} setIsEditing={setIsEditing} />,
      colId: 'actions',
      cellStyle: { padding: 0, border: 'none' },
      headerName: '',
      resizable: false,
      pinned: 'right',
      suppressHeaderFilterButton: true,
      suppressHeaderContextMenu: true,
      suppressMovable: true
    }
  ];
}
